import { triggerHoldCountdownHaptic } from "../../lib/native/haptics";
import type { Rule, RuleConfig, Target } from "../../lib/rules/ruleTypes";
import {
  isRuleActivelyEnforcingForSwipeGuard,
  isRuleFullyCoveredByAnotherEnforcingRule,
  isTargetFullyCoveredByAnotherEnforcingRule,
} from "./blockedItemsCoverage";
import { requestSwipeUnlockHighlight } from "./swipeUnlockHighlight";

const SWIPE_UNLOCK_NUDGE_HAPTIC_PULSES = 3;

export type SwipeUnlockGuardContext = {
  rules: Rule[];
  targets: Target[];
};

/**
 * True when the rule is actively enforcing ("in vigore") and direct mutations from
 * the Rules tab must go through the global swipe-to-unlock slider instead.
 *
 * When `context` is provided, returns false if another single actively enforcing rule
 * fully covers this rule's blocked apps and domains.
 */
export function requiresSwipeUnlockToMutateRule(
  rule: RuleConfig,
  now: Date = new Date(),
  context?: SwipeUnlockGuardContext,
): boolean {
  if (!isRuleActivelyEnforcingForSwipeGuard(rule, now)) return false;
  if (context) {
    if (isRuleFullyCoveredByAnotherEnforcingRule(rule as Rule, context.rules, context.targets, now)) {
      return false;
    }
  }
  return true;
}

/** True when at least one actively enforcing rule references `targetId`. */
export function isTargetLockedByEnforcingRules(
  targetId: string,
  rules: Rule[],
  targets: Target[],
  now: Date = new Date(),
): boolean {
  const target = targets.find((entry) => entry._id === targetId);
  if (target && isTargetFullyCoveredByAnotherEnforcingRule(target, rules, targets, now)) {
    return false;
  }

  return rules.some(
    (rule) =>
      rule.targetIds.includes(targetId) && isRuleActivelyEnforcingForSwipeGuard(rule, now),
  );
}

/** Pulse the swipe-to-unlock track and fire a brief triple haptic on native. */
export function nudgeSwipeUnlock(): void {
  requestSwipeUnlockHighlight();
  triggerHoldCountdownHaptic(SWIPE_UNLOCK_NUDGE_HAPTIC_PULSES);
}

export {
  isRuleActivelyEnforcingForSwipeGuard,
  isRuleFullyCoveredByAnotherEnforcingRule,
  isTargetFullyCoveredByAnotherEnforcingRule,
} from "./blockedItemsCoverage";
